import { OnInit, OnDestroy } from '@angular/core'
import { Component } from '@angular/core'
import { Subscription, interval } from 'rxjs'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { LiveUserAuthority } from 'src/lib/entity/live-user-authority'
import {
  GiftPointHistory,
  targetPeriod,
  PointHistoryPeriod,
  UsersOwnedPoint,
  ChangePageRequest,
} from 'src/lib/entity/gift-point'
import { ConsoleLogger } from 'src/lib/console-logger'
import { ActivatedRoute } from '@angular/router'
import { FirestoreAccess } from 'src/lib/firestore-access'
import {
  OrderResRouteParams,
  OrderMeta,
  OrderStatus,
} from 'src/lib/entity/order'
import { environment } from 'src/env/environment'
import { Location } from '@angular/common'

@Component({
  templateUrl: './point-manage-page.component.html',
  styleUrls: ['./point-manage-page.component.scss'],
})
export class PointManagePageComponent implements OnInit, OnDestroy {
  /* Subscription */
  private getAuthoritySub: Subscription | undefined
  private queryParamsSub: Subscription | undefined
  private getOrderStatusSub: Subscription | undefined
  private getGiftPointHistoriesSub1: Subscription | undefined
  private getGiftPointHistoriesSub2: Subscription | undefined
  private getGiftPointHistoriesSub3: Subscription | undefined

  authority: LiveUserAuthority | undefined
  baseURL = environment.baseURL
  loginCallbackURL =
    environment.baseURL.mag2mypage + 'Welcome.do?ref=' + window.location.href

  /* ログインセッションが30分で切れてしまうため、視聴画面から定期ポーリングする対応 */
  private authorityPollingMillis = 1000 * 60 * 10 // 10分
  private authorityPollingTimer: Subscription

  /* メンテナンスページ用 */
  isMaintenance = false

  /* ポイント関連 */
  targetPeriod: targetPeriod[]
  pointHistories: GiftPointHistory[] | 'NOT_FOUND'
  usersOwnedPoint: UsersOwnedPoint
  pointHistoryPeriod: PointHistoryPeriod | 'NOT_FOUND'

  /* ページ表示コントロール */
  isShowPointManagePage = true
  isShowChargePage = false
  isShowPointHistoryAllPage = false
  isShowPointHistoryDetailPage = false
  yearMonth: string

  /* チャージ後のデータ更新用 */
  private orderStatus: OrderStatus

  constructor(
    private logger: ConsoleLogger,
    private route: ActivatedRoute,
    private firestoreAccess: FirestoreAccess,
    private location: Location
  ) {}

  ngOnInit() {
    this.getAuthoritySub = MagliveApiClient.getAuthority().subscribe(
      (authorities) => {
        // 未ログインなのでログインページにリダイレクト
        if (authorities === 'NOT_LOGIN') {
          window.location.href = this.loginCallbackURL
          return
        }
        if (authorities.status === 503) {
          this.isMaintenance = true
        }
        if (authorities !== 'NOT_LOGIN') {
          this.authority = authorities
          this.usersOwnedPoint = authorities.ownedPoint
        } else {
          this.authorityPollingTimer = interval(
            this.authorityPollingMillis
          ).subscribe(() => {
            this.getAuthoritySub = MagliveApiClient.getAuthority().subscribe(
              (newAuthorities) => {
                if (newAuthorities === 'NOT_LOGIN') {
                  // 非ログイン状態になったら非有効化
                  this.authorityPollingTimer.unsubscribe()
                }
              }
            )
          })
        }
      }
    )

    // 3ヶ月分のポイント履歴取得
    this.getPointHistoryFor3Months()

    // 履歴のある月の一覧取得
    MagliveApiClient.getGiftPointHistoryPeriod().subscribe((value) => {
      this.pointHistoryPeriod = value
      this.logger.info(
        this.constructor.name,
        'getGiftPointHistoryPeriod',
        'pointHistoryPeriod',
        value
      )
    })

    // ポイントチャージ後のcallbackUrlで表示された場合の挙動
    this.queryParamsSub = this.route.queryParams.subscribe(
      (params: OrderResRouteParams) => {
        this.logger.info(
          this.constructor.name,
          'ngOnInit',
          'routeParams',
          params
        )
        // ordersのstatusを監視 (処理中表示のコントロール用)
        if (params.success !== undefined && params.orderId !== undefined) {
          this.getOrderStatusSub = this.firestoreAccess
            .getOrderStatus(params.orderId)
            .subscribe((value: OrderMeta[]) => {
              this.orderStatus = value[0].status
              this.checkShowSpinner()
              this.logger.info(
                this.constructor.name,
                'ngOnInit',
                'orderStatus',
                this.orderStatus
              )
            })
        }
      }
    )
  }

  ngOnDestroy(): void {
    if (this.authorityPollingTimer) {
      this.authorityPollingTimer.unsubscribe()
    }
    if (this.getAuthoritySub) {
      this.getAuthoritySub.unsubscribe()
    }
    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe()
    }
    if (this.getOrderStatusSub) {
      this.getOrderStatusSub.unsubscribe()
    }
    if (this.getGiftPointHistoriesSub1) {
      this.getGiftPointHistoriesSub1.unsubscribe()
    }
    if (this.getGiftPointHistoriesSub2) {
      this.getGiftPointHistoriesSub2.unsubscribe()
    }
    if (this.getGiftPointHistoriesSub3) {
      this.getGiftPointHistoriesSub3.unsubscribe()
    }
  }

  /**
   * 処理後のアップデートと完了アラートの表示
   */
  private checkShowSpinner() {
    if (
      this.orderStatus !== undefined &&
      this.orderStatus !== 'COMPLETED' &&
      this.orderStatus !== 'FAILED'
    ) {
      this.logger.message(
        this.constructor.name,
        'checkShowSpinner',
        'order status is processing...'
      )
    } else {
      this.logger.message(
        this.constructor.name,
        'checkShowSpinner',
        'order status is fixed'
      )
      // 所持ポイントをアップデート
      this.getAuthoritySub = MagliveApiClient.getAuthority().subscribe(
        (authorities) => {
          this.usersOwnedPoint = authorities.ownedPoint
          this.logger.message(
            this.constructor.name,
            'checkShowSpinner',
            'Users owned point updated.'
          )
          this.location.replaceState('point')
          if (alert('チャージ完了しました') === undefined) {
            return
          }
        }
      )
    }
  }

  /**
   * 3ヶ月分のポイント履歴取得
   */
  private getPointHistoryFor3Months() {
    this.targetPeriod = this.getTargetYearMonth()
    let currentMonth
    let lastMoth
    let monthBeforeLast
    let result = []

    this.getGiftPointHistoriesSub1 = MagliveApiClient.getGiftPointHistories(
      this.targetPeriod[0].year + '-' + this.targetPeriod[0].month
    ).subscribe((value) => {
      currentMonth = value
      this.getGiftPointHistoriesSub2 = MagliveApiClient.getGiftPointHistories(
        this.targetPeriod[1].year + '-' + this.targetPeriod[1].month
      ).subscribe((value) => {
        lastMoth = value
        this.getGiftPointHistoriesSub3 = MagliveApiClient.getGiftPointHistories(
          this.targetPeriod[2].year + '-' + this.targetPeriod[2].month
        ).subscribe((value) => {
          monthBeforeLast = value
          result.push(monthBeforeLast)
          result.push(lastMoth)
          result.push(currentMonth)
          this.pointHistories = result
          this.logger.info(
            this.constructor.name,
            'getGiftPointHistories',
            'pointHistoriesAll',
            this.pointHistories
          )
        })
      })
    })
  }

  /**
   * 現在月を含めた3ヶ月分の年月を返す
   */
  private getTargetYearMonth(): targetPeriod[] {
    const now = new Date()
    const lastMoth = new Date(now.getFullYear(), now.getMonth() - 1)
    const monthBeforeLast = new Date(now.getFullYear(), now.getMonth() - 2)
    return [
      {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      },
      {
        year: lastMoth.getFullYear(),
        month: lastMoth.getMonth() + 1,
      },
      {
        year: monthBeforeLast.getFullYear(),
        month: monthBeforeLast.getMonth() + 1,
      },
    ]
  }

  /**
   * ページの表示切替
   */
  changePage(request: ChangePageRequest) {
    if (request.name === 'CHARGE') {
      this.isShowChargePage = true
      this.isShowPointManagePage = false
      this.isShowPointHistoryAllPage = false
      this.isShowPointHistoryDetailPage = false
    } else if (request.name === 'HISTORY_ALL') {
      this.isShowChargePage = false
      this.isShowPointManagePage = false
      this.isShowPointHistoryAllPage = true
      this.isShowPointHistoryDetailPage = false
    } else if (request.name === 'HISTORY_DETAIL') {
      this.isShowChargePage = false
      this.isShowPointManagePage = false
      this.isShowPointHistoryAllPage = false
      this.isShowPointHistoryDetailPage = true
      this.yearMonth = request.yearMonth
    } else if (request.name === 'MANAGE') {
      this.isShowChargePage = false
      this.isShowPointManagePage = true
      this.isShowPointHistoryAllPage = false
      this.isShowPointHistoryDetailPage = false
    }
  }
}
